import React, { useCallback, useEffect, useState } from "react";
import { ContentType } from "@types";
import FullScreenWrapper from "../full-screen-wrapper/FullScreenWrapper";
import Video from "./video/Video";
import Audio from "./audio/Audio";
import Slide from "./slide/Slide";
import RevealSlideView from "./revealslide/Reveal";
import Form from "./form/Form";
import Pdf from "./pdf/Pdf";
import { useStudentNextContent } from "../useStudentNextContent";
import {
  SllModule,
  SllLesson,
  Lesson,
  Content,
} from "@api/graphql-codegen";
import { useIntercom } from "react-use-intercom";
import { useStudentLessonContent } from "@src/hooks/useStudentLessonContent";
import { useActivityTracker } from "@src/hooks/useActivity";
import ZipRevealSlideView from "./revealslide/ZipReveal";
import { logger } from '../../../utils/logger';

type Props = {
  content: Content | SllModule;
  lesson?: Lesson | SllLesson;
  goBack?: () => void;
  onExit?: () => void;
  isPreview?: boolean;
  lastPageNum?: number;
  isParentApp?: boolean;
};

function SelectedStudentContent(props: Props) {
  const {
    content,
    lesson,
    goBack,
    isPreview = false,
    lastPageNum,
  } = props;
  const { hasNextContent, nextContent, selectNext } = useStudentNextContent();
  const { pageNumber, setPageNumber } = useStudentLessonContent();
  const [curContent, setCurContent] = useState<SllModule | Content>();
  const [timer, setTimer] = useState<number>(30 * 60); // 30 minutes in seconds

  const { update } = useIntercom();
  const { pauseActivityTracker, resumeActivityTracker } = useActivityTracker();

  useEffect(() => {
    update?.({
      hideDefaultLauncher: true,
    });
    
  }, [isPreview, update]);

  useEffect(() => {
    if (lastPageNum) {
      setPageNumber(lastPageNum);
    }
  }, [lastPageNum, setPageNumber]);

  useEffect(() => {
    if (curContent?.id && curContent?.id !== content?.id) {
      setPageNumber(1);
    }
    setCurContent(content);
  }, [lastPageNum, curContent?.id, content, setCurContent, setPageNumber]);

  useEffect(() => {
    if (!isPreview) {
      // Pause activity tracker when a teacher starts a lesson
      pauseActivityTracker();
    }

    // Resume activity tracker when a teacher ends a lesson
    return () => resumeActivityTracker();
  }, [isPreview, pauseActivityTracker, resumeActivityTracker]);

  useEffect(() => {
    if (timer > 0) {
      const timerId = setInterval(() => {
        setTimer((prevTime) => prevTime - 1);
      }, 1000);

      return () => clearInterval(timerId); // Clear the interval on component unmount
    }
  }, [timer]);

  useEffect(() => {
    if (timer === 0) {
      resumeActivityTracker();
    }
  }, [resumeActivityTracker, timer]);

  const onNext = useCallback(() => {
    if (hasNextContent && !nextContent?.pauseHere) {
      selectNext(content);
    }
  }, [content, hasNextContent, nextContent?.pauseHere, selectNext]);

  const handlePageNumber = useCallback(
    (value: any) => {
      setPageNumber(value);
    },
    [setPageNumber]
  );

  const presentationContent = useCallback(
    (isFullScreen) => {
      if (content && content.data) {
        switch (content.type) {
          case ContentType.VIDEO: {
            return (
              <Video
                data={content.data!}
                key={content.id}
                isFullScreen={isFullScreen}
                metaData={content}
                onNext={onNext}
                isPreview={isPreview}
                goBack={goBack}
              />
            );
          }
          case ContentType.AUDIO: {
            return (
              <Audio
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
              />
            );
          }
          case ContentType.PDF: {
            return (
              <Pdf
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
              />
            );
          }
          case ContentType.SLIDE: {
            return (
              <Slide
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
              />
            );
          }
          case ContentType.REVEALSLIDE: {
            return (
              <RevealSlideView
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
              />
            );
          }
          case ContentType.ZIPSLIDE: {
            return (
              <ZipRevealSlideView
                data={content.data!}
                isFullScreen={isFullScreen}
                isPreview={isPreview}
                goBack={goBack}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
              />
            );
          }
          case ContentType.FORM: {
            return (
              <Form
                data={content.data!}
                formData={content.formData}
                isFullScreen={isFullScreen}
                id={content.id}
                name={content.name}
                isPreview={isPreview}
                goBack={goBack}
                pageNumber={pageNumber}
                changePageNumber={handlePageNumber}
              />
            );
          }
          default: {
            logger.debug(`[Not supported type]: `, content.type);
          }
        }
      }
    },
    [
      content,
      onNext,
      isPreview,
      goBack,
      pageNumber,
      handlePageNumber,
    ]
  );

  return (
    <>
      <FullScreenWrapper lesson={lesson} content={content} goBack={goBack} isFullscreen={true}>
        {presentationContent}
      </FullScreenWrapper>
    </>
  );
}

export default SelectedStudentContent;
